// frontend/modules/sms/hooks/useSms.ts
import { useCallback, useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTheme } from '@/components/common/ThemeProvider';
import { showSuccessNotification, showErrorNotification } from '@/components/common/utils/notify';
import { useGetSmsBalance, useSendSingleSms, useSendMultipleMessages } from "@/shared/api/client/sms-client-controller";
import {
    useGetFakerSmsLogs,
    getGetFakerSmsLogsQueryKey,
    useClearFakerSmsLogs,
} from '@/shared/api/client/sms-portal';
import { useSmsStore } from "@/modules/sms/stores/sms-store";


export const useSms = () => {
    const { dark } = useTheme();
    const queryClient = useQueryClient();
    const [isClearingLogs, setIsClearingLogs] = useState(false);

    // Get state and actions from store
    const {
        message,
        recipients,
        senderId,
        reference,
        isMultiMode,
        isSending,
        smsBalance,
        isLoadingBalance,
        fakerLogs,
        fakerLogsPage,
        fakerLogsPageSize,
        fakerLogsTotalElements,
        isLoadingFakerLogs,

        setMessage,
        setSenderId,
        setReference,
        setIsMultiMode,
        addRecipient,
        removeRecipient,
        updateRecipient,
        clearForm,
        setIsSending,
        setSmsBalance,
        setIsLoadingBalance,
        setFakerLogs,
        setFakerLogsPage,
        setFakerLogsPageSize,
        setFakerLogsTotalElements,
        setIsLoadingFakerLogs,
    } = useSmsStore();

    // Get mutations and queries from Orval generated hooks
    const { mutateAsync: sendSingleSms, isPending: isSendingSingle } = useSendSingleSms();
    const { mutateAsync: sendMultiMessages, isPending: isSendingMulti } = useSendMultipleMessages();
    const { data: balanceData, isLoading: isLoadingBalanceData, refetch: refetchBalance } = useGetSmsBalance();
    const { mutateAsync: clearFakerLogsApi } = useClearFakerSmsLogs();

    // Institution SMS balance (from portal endpoint)
    const {
        data: institutionBalanceData,
        isLoading: isLoadingInstitutionBalance,
        refetch: refetchInstitutionBalance,
    } = useGetSmsBalance();

    // Faker SMS logs (paginated)
    const {
        data: fakerLogsData,
        isLoading: isLoadingFakerLogsData,
        refetch: refetchFakerLogs,
    } = useGetFakerSmsLogs({ page: fakerLogsPage, size: fakerLogsPageSize });

    // Update isSending state based on mutations
    useEffect(() => {
        setIsSending(isSendingSingle || isSendingMulti);
    }, [isSendingSingle, isSendingMulti, setIsSending]);

    // Update balance when data is loaded
    useEffect(() => {
        if (balanceData) {
            console.log(balanceData?.remainingBalance)
            // @ts-ignore
            setSmsBalance(balanceData?.remainingBalance);
        }
    }, [balanceData, setSmsBalance]);

    // Update isLoadingBalance
    useEffect(() => {
        setIsLoadingBalance(isLoadingBalanceData || isLoadingInstitutionBalance);
    }, [isLoadingBalanceData, isLoadingInstitutionBalance, setIsLoadingBalance]);

    // Update institution balance from portal endpoint
    useEffect(() => {
        if (institutionBalanceData?.remainingBalance !== undefined) {
            setSmsBalance(institutionBalanceData.remainingBalance!);
        }
    }, [institutionBalanceData, setSmsBalance]);

    // Update faker logs
    useEffect(() => {
        setIsLoadingFakerLogs(isLoadingFakerLogsData);
    }, [isLoadingFakerLogsData, setIsLoadingFakerLogs]);

    useEffect(() => {
        if (fakerLogsData) {
            setFakerLogs(fakerLogsData.content ?? []);
            setFakerLogsTotalElements(fakerLogsData.totalElements ?? 0);
        }
    }, [fakerLogsData, setFakerLogs, setFakerLogsTotalElements]);

    // Load SMS balance
    const loadSmsBalance = useCallback(() => {
        refetchBalance();
        refetchInstitutionBalance();
    }, [refetchBalance, refetchInstitutionBalance]);

    // Refresh faker SMS logs
    const refreshFakerLogs = useCallback(() => {
        queryClient.invalidateQueries({ queryKey: getGetFakerSmsLogsQueryKey() });
    }, [queryClient]);

    // Handle faker logs page change
    const onFakerLogsPageChange = useCallback((page: number, size: number) => {
        setFakerLogsPage(page - 1); // antd pagination is 1-based
        setFakerLogsPageSize(size);
    }, [setFakerLogsPage, setFakerLogsPageSize]);

    // Clear faker logs
    const clearFakerLogs = useCallback(async () => {
        try {
            setIsClearingLogs(true);
            await clearFakerLogsApi();
            showSuccessNotification(
                'Success',
                'All faker SMS logs have been cleared',
                dark
            );
            refreshFakerLogs();
        } catch (error) {
            console.error('Error clearing faker logs:', error);
            showErrorNotification(
                'Error',
                'Failed to clear faker SMS logs. Please try again.',
                dark
            );
        } finally {
            setIsClearingLogs(false);
        }
    }, [clearFakerLogsApi, refreshFakerLogs, dark]);

    // Initialize - load balance
    useEffect(() => {
        loadSmsBalance();
    }, [loadSmsBalance]);

    // Send SMS - handles both single and multiple
    const sendSms = useCallback(async () => {
        // Validate message
        if (!message.trim()) {
            showErrorNotification('Error', 'Message cannot be empty', dark);
            return;
        }

        // Validate sender ID
        if (!senderId.trim()) {
            showErrorNotification('Error', 'Sender ID cannot be empty', dark);
            return;
        }

        // Validate recipients
        const validRecipients = recipients.filter(r => r.valid);
        if (validRecipients.length === 0) {
            showErrorNotification('Error', 'No valid recipients provided', dark);
            return;
        }

        try {
            let response;

            if (isMultiMode) {
                // Handle multiple messages with different content (not implemented in UI yet)
                showErrorNotification('Error', 'Multi-message mode is not yet implemented in UI', dark);
                return;
            } else {
                // Simple mode - same message to all recipients
                if (validRecipients.length === 1) {
                    // Single recipient
                    response = await sendSingleSms({
                        data: {
                            senderId,
                            phoneNumber: validRecipients[0].phoneNumber,
                            text: message,
                            reference
                        }
                    });

                    showSuccessNotification(
                        'SMS Sent',
                        `Message sent successfully to ${validRecipients[0].phoneNumber}`,
                        dark
                    );
                } else {
                    // Multiple recipients with the same message
                    response = await sendMultiMessages({
                        data: {
                            messages: [{
                                senderId,
                                recipients: validRecipients.map(r => r.phoneNumber),
                                text: message
                            }],
                            reference
                        }
                    });

                    showSuccessNotification(
                        'SMS Sent',
                        `Message sent successfully to ${validRecipients.length} recipients`,
                        dark
                    );
                }
            }

            console.log('SMS sent response:', response);

            // Clear form and refresh balance
            clearForm();
            loadSmsBalance();

        } catch (error: any) {
            console.error('Error sending SMS:', error);

            // Extract the actual error message from the backend response
            let errorMessage = 'Failed to send SMS. Please try again later.';
            if (error?.response?.data?.message) {
                errorMessage = error.response.data.message;
            } else if (error?.message) {
                errorMessage = error.message;
            }

            showErrorNotification(
                'Error',
                errorMessage,
                dark
            );
        }
    }, [
        message,
        senderId,
        recipients,
        reference,
        isMultiMode,
        sendSingleSms,
        sendMultiMessages,
        clearForm,
        loadSmsBalance,
        dark
    ]);

    return {
        // State
        message,
        recipients,
        senderId,
        reference,
        isMultiMode,
        isSending,
        smsBalance,
        isLoadingBalance,
        fakerLogs,
        fakerLogsPage,
        fakerLogsPageSize,
        fakerLogsTotalElements,
        isLoadingFakerLogs,
        isClearingLogs,

        // Actions
        setMessage,
        setSenderId,
        setReference,
        setIsMultiMode,
        addRecipient,
        removeRecipient,
        updateRecipient,
        clearForm,
        sendSms,
        loadSmsBalance,
        refreshFakerLogs,
        onFakerLogsPageChange,
        clearFakerLogs,
    };
};